import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { header } from '../components/header.encry';

const API_URL = process.env.REACT_APP_PEGASUS_API_URL;

const emptySettings = {
  enabled: false,
  shipping_zones_enabled: true,
  timezone: 'Africa/Casablanca',
  slot_minutes: 60,
  regions: [],
  shipping_zones: [],
  weekly_planning: [],
  dates: [],
  holidays: [],
  google_maps_api_key: '',
  map_zones: [],
};

const slotOptions = [15, 30, 45, 60, 90, 120];

const normalizeSettings = (settings = {}) => ({
  enabled: settings.enabled === true || settings.enabled === '1',
  shipping_zones_enabled: settings.shipping_zones_enabled !== false && settings.shipping_zones_enabled !== '0',
  timezone: settings.timezone || 'Africa/Casablanca',
  slot_minutes: Number(settings.slot_minutes || settings.slotMinutes || 60),
  regions: Array.isArray(settings.regions) ? settings.regions : [],
  shipping_zones: Array.isArray(settings.shipping_zones || settings.shippingZones)
    ? (settings.shipping_zones || settings.shippingZones).map((zone) => ({
      ...zone,
      livrable: zone?.livrable !== false && zone?.livrable !== '0',
      allow_delivery_date: zone?.allow_delivery_date !== false && zone?.allow_delivery_date !== '0',
      morocco_only: zone?.morocco_only === true || zone?.morocco_only === '1',
    }))
    : [],
  weekly_planning: normalizeWeeklyPlanning(settings.weekly_planning || settings.weeklyPlanning),
  dates: Array.isArray(settings.dates) ? settings.dates : [],
  holidays: Array.isArray(settings.holidays) ? settings.holidays : [],
  google_maps_api_key: settings.google_maps_api_key || settings.googleMapsApiKey || '',
  map_zones: Array.isArray(settings.map_zones || settings.mapZones) ? (settings.map_zones || settings.mapZones) : [],
});

const weekdayOptions = [
  { value: 'monday', label: 'Lundi' },
  { value: 'tuesday', label: 'Mardi' },
  { value: 'wednesday', label: 'Mercredi' },
  { value: 'thursday', label: 'Jeudi' },
  { value: 'friday', label: 'Vendredi' },
  { value: 'saturday', label: 'Samedi' },
  { value: 'sunday', label: 'Dimanche' },
];

const defaultWeeklyPlanning = weekdayOptions.map((day, index) => ({
  day: day.value,
  from: '09:00',
  to: '18:00',
  enabled: index < 6,
}));

const normalizeWeeklyPlanning = (rows = []) => {
  const byDay = new Map((Array.isArray(rows) ? rows : []).map((row) => [row.day, row]));
  return defaultWeeklyPlanning.map((defaultRow) => ({ ...defaultRow, ...(byDay.get(defaultRow.day) || {}) }));
};

const newDateExceptionRow = () => ({ date: '', from: '09:00', to: '18:00' });
const newHolidayRow = () => ({ from: '', to: '' });
const newShippingZoneRow = () => ({ code: '', name: '', livrable: true, allow_delivery_date: true, morocco_only: false });
const newMapZoneRow = () => ({ code: '', name: '', geometry: null, lat: '', lng: '', radius_km: '', livrable: true });

const googleMapsScriptId = 'pegasus-admin-google-maps-script';
let googleMapsLoaderPromise = null;

const loadGoogleMapsScript = (apiKey) => {
  if (!apiKey) return Promise.reject(new Error('missing_api_key'));
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (googleMapsLoaderPromise) return googleMapsLoaderPromise;

  googleMapsLoaderPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(googleMapsScriptId);
    if (existing) {
      existing.addEventListener('load', () => resolve(window.google.maps), { once: true });
      existing.addEventListener('error', reject, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = googleMapsScriptId;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve(window.google.maps);
    script.onerror = reject;
    document.head.appendChild(script);
  });

  return googleMapsLoaderPromise;
};

const closeLinearRing = (ring = []) => {
  const points = ring
    .map((point) => [Number(point?.[0]), Number(point?.[1])])
    .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat));
  if (points.length < 3) return [];
  const first = points[0];
  const last = points[points.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    points.push([...first]);
  }
  return points.length >= 4 ? points : [];
};

const normalizePolygonGeometry = (geometry) => {
  if (!geometry || geometry.type !== 'Polygon' || !Array.isArray(geometry.coordinates)) return null;
  const rings = geometry.coordinates.map(closeLinearRing).filter((ring) => ring.length >= 4);
  return rings.length ? { type: 'Polygon', coordinates: rings } : null;
};

const geometryToGooglePaths = (geometry) => {
  const polygon = normalizePolygonGeometry(geometry);
  if (!polygon) return [];
  return polygon.coordinates.map((ring) => ring.map(([lng, lat]) => ({ lat, lng })));
};

const polygonPointCount = (geometry) => {
  const polygon = normalizePolygonGeometry(geometry);
  if (!polygon) return 0;
  return polygon.coordinates.reduce((total, ring) => total + Math.max(0, ring.length - 1), 0);
};

export default function WooDeliverySettingsView() {
  const [settings, setSettings] = useState(emptySettings);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [updatedAt, setUpdatedAt] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [mapError, setMapError] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [citySearching, setCitySearching] = useState(false);
  const [expandedDeliveryCities, setExpandedDeliveryCities] = useState({});
  const [activeTab, setActiveTab] = useState('map');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const overlaysRef = useRef([]);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [response, zonesResponse] = await Promise.all([
        axios.get(`${API_URL}/woocommerce/delivery-settings/admin`, header()),
        axios.get(`${API_URL}/delivery-zones`, header()).catch(() => ({ data: { response: [] } })),
      ]);
      const payload = response.data?.response || {};
      setSettings(normalizeSettings(payload.settings));
      setDeliveryZones(Array.isArray(zonesResponse.data?.response) ? zonesResponse.data.response : []);
      setUpdatedAt(payload.updated_at || '');
    } catch (error) {
      console.error('Load Woo delivery settings error', error);
      toast.error('Impossible de charger les reglages livraison WooCommerce.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  useEffect(() => {
    if (!settings.google_maps_api_key || !mapContainerRef.current) {
      setMapReady(false);
      return;
    }

    let mounted = true;
    setMapError('');
    loadGoogleMapsScript(settings.google_maps_api_key)
      .then((maps) => {
        if (!mounted || !mapContainerRef.current) return;
        if (!mapRef.current) {
          mapRef.current = new maps.Map(mapContainerRef.current, {
            center: { lat: 33.5731, lng: -7.5898 },
            zoom: 10,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: true,
          });
        }
        setMapReady(true);
      })
      .catch((error) => {
        console.error('Google Maps admin load error', error);
        if (mounted) {
          setMapReady(false);
          setMapError('Impossible de charger Google Maps. Verifiez la cle API.');
        }
      });

    return () => {
      mounted = false;
    };
  }, [settings.google_maps_api_key]);

  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayRow = (field, index, key, value) => {
    setSettings((prev) => {
      const rows = field === 'weekly_planning' ? normalizeWeeklyPlanning(prev[field]) : [...(prev[field] || [])];
      rows[index] = { ...(rows[index] || {}), [key]: value };
      return { ...prev, [field]: rows };
    });
  };

  const addRow = (field, row) => {
    setSettings((prev) => ({ ...prev, [field]: [...(prev[field] || []), row] }));
  };

  const removeRow = (field, index) => {
    setSettings((prev) => ({ ...prev, [field]: (prev[field] || []).filter((_, idx) => idx !== index) }));
  };

  const updateDeliveryZoneWooCode = async (zoneId, wooZoneCode) => {
    const nextCode = String(wooZoneCode || '').trim().toUpperCase();
    setDeliveryZones((prev) => prev.map((zone) => (zone._id === zoneId ? { ...zone, woo_zone_code: nextCode } : zone)));
    try {
      const response = await axios.patch(`${API_URL}/delivery-zones/${zoneId}`, { woo_zone_code: nextCode }, header());
      const updatedZone = response.data?.response;
      if (updatedZone?._id) {
        setDeliveryZones((prev) => prev.map((zone) => (zone._id === zoneId ? updatedZone : zone)));
      }
      toast.success(nextCode ? `Zone liee a ${nextCode}` : 'Lien WooCommerce retire.');
    } catch (error) {
      console.error('Update delivery zone Woo code error', error);
      toast.error(error?.response?.data?.error || 'Impossible de lier cette zone.');
      loadSettings();
    }
  };

  const wooZoneCodes = new Set((settings.shipping_zones || []).map((zone) => String(zone.code || '').trim().toUpperCase()).filter(Boolean));
  const linkedWooCode = (zone) => {
    const candidate = String(zone.woo_zone_code || zone.code || '').trim().toUpperCase();
    return wooZoneCodes.has(candidate) ? candidate : '';
  };

  const settingsPolygonZones = (settings.map_zones || [])
    .map((zone, index) => ({ ...zone, _polygon_key: `map-zone-${index}`, _map_zone_index: index, _polygon_source: 'map_settings' }))
    .filter((zone) => normalizePolygonGeometry(zone.geometry));

  const allPolygonZones = [
    ...(deliveryZones || []).map((zone) => ({ ...zone, _polygon_key: zone._id, _polygon_source: 'delivery_collection' })),
    ...settingsPolygonZones,
  ];

  const deliveryZonesByCity = allPolygonZones.reduce((groups, zone) => {
    const city = zone.cityName || zone.city_name || zone.city || zone.name || 'Sans ville';
    if (!groups[city]) groups[city] = [];
    groups[city].push(zone);
    return groups;
  }, {});

  const deliveryCityGroups = Object.entries(deliveryZonesByCity)
    .map(([cityName, zones]) => ({
      cityName,
      zones: [...zones].sort((left, right) => String(left.name || '').localeCompare(String(right.name || ''))),
      linkedCount: zones.filter((zone) => linkedWooCode(zone)).length,
      zoneCodes: Array.from(new Set(zones.map((zone) => linkedWooCode(zone)).filter(Boolean))).sort(),
    }))
    .sort((left, right) => left.cityName.localeCompare(right.cityName));

  const toggleDeliveryCity = (cityName) => {
    setExpandedDeliveryCities((prev) => ({ ...prev, [cityName]: !prev[cityName] }));
  };

  const saveDeliverySettingsSnapshot = async (nextSettings) => {
    const settingsToSave = {
      ...nextSettings,
      map_zones: (nextSettings.map_zones || []).filter((zone) => zone?.source !== 'delivery_zones_collection'),
    };
    const response = await axios.put(`${API_URL}/woocommerce/delivery-settings`, { settings: settingsToSave }, header());
    const payload = response.data?.response || {};
    const normalized = normalizeSettings(payload.settings || settingsToSave);
    setSettings(normalized);
    setUpdatedAt(payload.updated_at || '');
    return normalized;
  };

  const updateMapSettingsZoneWooCode = async (zoneIndex, wooZoneCode) => {
    const nextCode = String(wooZoneCode || '').trim().toUpperCase();
    const previousSettings = settings;
    const nextSettings = {
      ...settings,
      map_zones: (settings.map_zones || []).map((zone, index) => (
        index === zoneIndex ? { ...zone, code: nextCode, woo_zone_code: nextCode } : zone
      )),
    };
    setSettings(nextSettings);
    try {
      await saveDeliverySettingsSnapshot(nextSettings);
      toast.success(nextCode ? `Zone liee a ${nextCode}` : 'Lien WooCommerce retire.');
    } catch (error) {
      console.error('Update map settings zone Woo code error', error);
      toast.error(error?.response?.data?.error || 'Impossible de lier cette zone.');
      setSettings(previousSettings);
    }
  };

  const updatePolygonZoneWooCode = async (zone, wooZoneCode) => {
    if (zone._polygon_source === 'map_settings') {
      await updateMapSettingsZoneWooCode(zone._map_zone_index, wooZoneCode);
      return;
    }
    await updateDeliveryZoneWooCode(zone._id, wooZoneCode);
  };

  const updateDeliveryCityWooCode = async (cityName, wooZoneCode) => {
    const zones = deliveryZonesByCity[cityName] || [];
    const nextCode = String(wooZoneCode || '').trim().toUpperCase();
    if (!zones.length) return;

    const previousZones = deliveryZones;
    const previousSettings = settings;
    const collectionZones = zones.filter((zone) => zone._polygon_source !== 'map_settings');
    const mapZoneIndexes = zones
      .filter((zone) => zone._polygon_source === 'map_settings')
      .map((zone) => zone._map_zone_index);

    setDeliveryZones((prev) => prev.map((zone) => (collectionZones.some((item) => item._id === zone._id) ? { ...zone, woo_zone_code: nextCode } : zone)));
    const nextSettings = {
      ...settings,
      map_zones: (settings.map_zones || []).map((zone, index) => (
        mapZoneIndexes.includes(index) ? { ...zone, code: nextCode, woo_zone_code: nextCode } : zone
      )),
    };
    if (mapZoneIndexes.length) {
      setSettings(nextSettings);
    }
    try {
      await Promise.all(collectionZones.map((zone) => axios.patch(`${API_URL}/delivery-zones/${zone._id}`, { woo_zone_code: nextCode }, header())));
      if (mapZoneIndexes.length) {
        await saveDeliverySettingsSnapshot(nextSettings);
      }
      toast.success(nextCode ? `${zones.length} polygone(s) de ${cityName} lies a ${nextCode}` : `${zones.length} lien(s) retires pour ${cityName}`);
      loadSettings();
    } catch (error) {
      console.error('Update delivery city Woo code error', error);
      toast.error(error?.response?.data?.error || 'Impossible de lier cette ville.');
      setDeliveryZones(previousZones);
      setSettings(previousSettings);
    }
  };

  const renderMapZones = useCallback(() => {
    const maps = window.google?.maps;
    if (!maps || !mapRef.current) return;

    overlaysRef.current.forEach((overlay) => overlay.setMap(null));
    overlaysRef.current = [];

    const bounds = new maps.LatLngBounds();
    let hasBounds = false;
    (deliveryZones || []).forEach((zone) => {
      const paths = geometryToGooglePaths(zone.geometry);
      if (!paths.length) return;
      const linked = Boolean(zone.woo_zone_code);
      const polygon = new maps.Polygon({
        map: mapRef.current,
        paths,
        strokeColor: linked ? '#0d6efd' : '#6c757d',
        strokeOpacity: 0.8,
        strokeWeight: linked ? 2 : 1,
        fillColor: linked ? '#0d6efd' : '#6c757d',
        fillOpacity: linked ? 0.12 : 0.05,
      });
      const info = new maps.InfoWindow({
        content: `<strong>${zone.name || 'Zone livraison'}</strong><br>Code Woo: ${zone.woo_zone_code || 'non lie'}<br>${polygonPointCount(zone.geometry)} points`,
      });
      polygon.addListener('click', (event) => {
        info.setPosition(event.latLng);
        info.open(mapRef.current);
      });
      overlaysRef.current.push(polygon);
      paths.flat().forEach((point) => {
        bounds.extend(point);
        hasBounds = true;
      });
    });

    (settings.map_zones || []).forEach((zone) => {
      if (zone.source === 'delivery_zones_collection') return;
      const paths = geometryToGooglePaths(zone.geometry);
      if (paths.length) {
        const polygon = new maps.Polygon({
          map: mapRef.current,
          paths,
          strokeColor: zone.livrable === false ? '#dc3545' : '#198754',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: zone.livrable === false ? '#dc3545' : '#198754',
          fillOpacity: 0.15,
        });
        const info = new maps.InfoWindow({
          content: `<strong>${zone.name || zone.code || 'Zone livrable'}</strong><br>${polygonPointCount(zone.geometry)} points`,
        });
        polygon.addListener('click', (event) => {
          info.setPosition(event.latLng);
          info.open(mapRef.current);
        });
        overlaysRef.current.push(polygon);
        paths.flat().forEach((point) => {
          bounds.extend(point);
          hasBounds = true;
        });
        return;
      }

      const lat = Number(zone.lat);
      const lng = Number(zone.lng);
      const radius = Number(zone.radius_km);
      if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(radius) || radius <= 0) return;

      const circle = new maps.Circle({
        map: mapRef.current,
        center: { lat, lng },
        radius: radius * 1000,
        strokeColor: zone.livrable === false ? '#dc3545' : '#198754',
        strokeOpacity: 0.85,
        strokeWeight: 2,
        fillColor: zone.livrable === false ? '#dc3545' : '#198754',
        fillOpacity: 0.12,
      });
      const marker = new maps.Marker({
        map: mapRef.current,
        position: { lat, lng },
        title: zone.name || zone.code || 'Zone livrable',
      });
      overlaysRef.current.push(circle, marker);
      bounds.union(circle.getBounds());
      hasBounds = true;
    });

    if (hasBounds) {
      mapRef.current.fitBounds(bounds);
    }
  }, [deliveryZones, settings.map_zones]);

  useEffect(() => {
    if (!mapReady || !window.google?.maps || !mapRef.current) return;
    renderMapZones();
  }, [mapReady, settings.map_zones, deliveryZones, renderMapZones]);

  useEffect(() => {
    if (activeTab !== 'map' || !mapReady || !window.google?.maps || !mapRef.current) return;
    window.google.maps.event.trigger(mapRef.current, 'resize');
    renderMapZones();
  }, [activeTab, mapReady, renderMapZones]);

  const addCityMapZone = async () => {
    if (!citySearch.trim()) {
      toast.warning('Entrez une ville ou une zone a chercher.');
      return;
    }
    setCitySearching(true);
    try {
      const response = await axios.get(`${API_URL}/woocommerce/delivery-settings/osm-boundary`, {
        ...header(),
        params: { city: citySearch.trim(), country: 'Morocco' },
      });
      const boundary = response.data?.response || {};
      const geometry = normalizePolygonGeometry(boundary.geometry);
      if (!geometry) {
        throw new Error('missing_polygon');
      }
      const matchedShippingZone = (settings.shipping_zones || []).find((zone) => {
        const zoneName = String(zone.name || '').toLowerCase();
        const haystack = `${boundary.name || ''} ${boundary.display_name || ''}`.toLowerCase();
        return zoneName && haystack.includes(zoneName);
      });
      const nextZone = {
        code: matchedShippingZone?.code || `MAP${String((settings.map_zones || []).length + 1).padStart(3, '0')}`,
        name: matchedShippingZone?.name || boundary.name || citySearch.trim(),
        geometry,
        source: 'osm_boundary',
        osm_id: boundary.id || '',
        display_name: boundary.display_name || '',
        lat: '',
        lng: '',
        radius_km: '',
        livrable: true,
      };

      setSettings((prev) => ({ ...prev, map_zones: [...(prev.map_zones || []), nextZone] }));
      toast.success(`Zone polygonale ajoutee: ${nextZone.name}`);
    } catch (error) {
      console.error('OSM boundary search error', error);
      toast.error(error?.response?.data?.error || 'Limites administratives introuvables pour cette ville.');
    } finally {
      setCitySearching(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const settingsToSave = {
        ...settings,
        map_zones: (settings.map_zones || []).filter((zone) => zone?.source !== 'delivery_zones_collection'),
      };
      const response = await axios.put(`${API_URL}/woocommerce/delivery-settings`, { settings: settingsToSave }, header());
      const payload = response.data?.response || {};
      const nextSettings = normalizeSettings(payload.settings);
      if (!nextSettings.google_maps_api_key && settings.google_maps_api_key) {
        nextSettings.google_maps_api_key = settings.google_maps_api_key;
      }
      setSettings(nextSettings);
      setUpdatedAt(payload.updated_at || '');
      toast.success('Reglages livraison WooCommerce enregistres.');
    } catch (error) {
      console.error('Save Woo delivery settings error', error);
      toast.error(error?.response?.data?.error || 'Erreur pendant l enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const deliveryTabs = [
    { key: 'map', label: 'Carte zones livrables', count: allPolygonZones.filter((zone) => linkedWooCode(zone)).length },
    { key: 'general', label: 'General', count: settings.enabled ? 1 : 0 },
    { key: 'wooZones', label: 'Zones WooCommerce Maroc', count: settings.shipping_zones.length },
    { key: 'mapZones', label: 'Zones Google Maps', count: settings.map_zones.length },
    { key: 'polygons', label: 'Polygones par ville', count: deliveryCityGroups.length },
    { key: 'weekly', label: 'Planning hebdomadaire', count: normalizeWeeklyPlanning(settings.weekly_planning).filter((row) => row.enabled !== false && row.enabled !== '0').length },
    { key: 'dates', label: 'Exceptions par date', count: settings.dates.length },
    { key: 'holidays', label: 'Jours feries', count: settings.holidays.length },
    { key: 'json', label: 'JSON', count: 0 },
  ];

  const tabPanelStyle = (key) => ({ display: activeTab === key ? 'block' : 'none' });

  if (loading) {
    return <div className="container-fluid mt-3">Chargement...</div>;
  }

  return (
    <div className="container-fluid mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2 className="mb-1">Livraison WooCommerce</h2>
          <div className="text-muted">
            Reglage central Pegasus synchronise vers le plugin WooCommerce. Derniere mise a jour: {updatedAt ? new Date(updatedAt).toLocaleString() : '-'}
          </div>
        </div>
        <button className="btn btn-success" onClick={saveSettings} disabled={saving}>
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-body py-2">
          <div className="d-flex flex-wrap gap-2">
            {deliveryTabs.map((tab) => (
              <button
                key={tab.key}
                type="button"
                className={`btn btn-sm ${activeTab === tab.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
                {tab.count > 0 && <span className={`badge ms-2 ${activeTab === tab.key ? 'bg-light text-primary' : 'bg-secondary'}`}>{tab.count}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={tabPanelStyle('map')}>
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div>
            <strong>Carte des zones livrables</strong>
            <div className="text-muted small">Cherchez une ville. Pegasus recupere sa frontiere OSM et sauvegarde un polygone pour valider la localisation au checkout.</div>
          </div>
          <span className={`badge ${mapReady ? 'bg-success' : 'bg-secondary'}`}>{mapReady ? 'Carte active' : 'Carte en attente'}</span>
        </div>
        <div className="card-body">
          <div className="row g-3 align-items-end mb-3">
            <div className="col-lg-5">
              <label className="form-label">Google Maps API key</label>
              <input
                className="form-control"
                value={settings.google_maps_api_key}
                onChange={(event) => updateField('google_maps_api_key', event.target.value)}
                placeholder="AIza..."
                autoComplete="off"
              />
              <div className="text-muted small">Necessaire pour afficher la carte. Les limites de ville sont recuperees via OpenStreetMap.</div>
            </div>
            <div className="col-lg-5">
              <label className="form-label">Ville ou zone a ajouter</label>
              <input
                className="form-control"
                value={citySearch}
                onChange={(event) => setCitySearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    addCityMapZone();
                  }
                }}
                placeholder="Casablanca, Bouskoura, Rabat..."
              />
            </div>
            <div className="col-lg-2">
              <button className="btn btn-primary w-100" onClick={addCityMapZone} disabled={citySearching}>
                {citySearching ? 'Recherche...' : 'Ajouter'}
              </button>
            </div>
          </div>
          {mapError && <div className="alert alert-warning py-2">{mapError}</div>}
          {!settings.google_maps_api_key && (
            <div className="alert alert-info py-2">Ajoutez la Google Maps API key pour activer la carte et la recherche par ville.</div>
          )}
          <div
            ref={mapContainerRef}
            style={{
              minHeight: 430,
              borderRadius: 18,
              overflow: 'hidden',
              background: '#eef2f7',
              border: '1px solid #dce3ec',
            }}
          />
          <div className="text-muted small mt-2">Les nouvelles zones utilisent des polygones de frontiere. Les anciennes zones en rayon restent affichees uniquement pour compatibilite.</div>
        </div>
      </div>
      </div>

      <div style={tabPanelStyle('general')}>
      <div className="card mb-3">
        <div className="card-body">
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={settings.enabled}
              onChange={(event) => updateField('enabled', event.target.checked)}
              id="wooDeliveryEnabled"
            />
            <label className="form-check-label fw-bold" htmlFor="wooDeliveryEnabled">
              Activer les champs livraison au checkout
            </label>
            <div className="text-muted small">Si desactive, le plugin garde le chat mais ne demande plus la date au checkout. La region reste geree par WooCommerce via shipping_state.</div>
          </div>
          <div className="form-check form-switch mb-3">
            <input
              className="form-check-input"
              type="checkbox"
              checked={settings.shipping_zones_enabled}
              onChange={(event) => updateField('shipping_zones_enabled', event.target.checked)}
              id="wooShippingZonesEnabled"
            />
            <label className="form-check-label fw-bold" htmlFor="wooShippingZonesEnabled">
              Activer les zones de livraison WooCommerce Maroc
            </label>
            <div className="text-muted small">Ces zones remplissent le champ natif WooCommerce shipping_state, sans champ region Pegasus separe.</div>
          </div>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Fuseau horaire</label>
              <input
                className="form-control"
                value={settings.timezone}
                onChange={(event) => updateField('timezone', event.target.value)}
                placeholder="Africa/Casablanca"
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">Taille des creneaux</label>
              <select
                className="form-select"
                value={settings.slot_minutes}
                onChange={(event) => updateField('slot_minutes', Number(event.target.value))}
              >
                {slotOptions.map((minutes) => (
                  <option key={minutes} value={minutes}>{minutes} minutes</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
      </div>

      <div className="row g-3">
        <div className="col-12">
          <div style={tabPanelStyle('wooZones')}>
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Zones WooCommerce Maroc</strong>
              <button className="btn btn-sm btn-outline-primary" onClick={() => addRow('shipping_zones', newShippingZoneRow())}>Ajouter une zone</button>
            </div>
            <div className="table-responsive">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 160 }}>Code</th>
                    <th>Nom</th>
                    <th style={{ width: 150 }}>Livrable checkout</th>
                    <th style={{ width: 150 }}>Date au checkout</th>
                    <th style={{ width: 170 }}>Maroc uniquement</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {settings.shipping_zones.length === 0 && (
                    <tr><td colSpan="6" className="text-muted">Aucune zone WooCommerce configuree.</td></tr>
                  )}
                  {settings.shipping_zones.map((row, index) => (
                    <tr key={`shipping-zone-${index}`}>
                      <td>
                        <input
                          className="form-control"
                          value={row.code || ''}
                          onChange={(event) => updateArrayRow('shipping_zones', index, 'code', event.target.value.toUpperCase())}
                          placeholder="MA001"
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={row.name || ''}
                          onChange={(event) => updateArrayRow('shipping_zones', index, 'name', event.target.value)}
                          placeholder="Casablanca"
                        />
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={row.livrable !== false && row.livrable !== '0'}
                            onChange={(event) => updateArrayRow('shipping_zones', index, 'livrable', event.target.checked)}
                            id={`shipping-zone-livrable-${index}`}
                          />
                          <label className="form-check-label" htmlFor={`shipping-zone-livrable-${index}`}>
                            Livrable
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={row.allow_delivery_date !== false && row.allow_delivery_date !== '0'}
                            onChange={(event) => updateArrayRow('shipping_zones', index, 'allow_delivery_date', event.target.checked)}
                            id={`shipping-zone-date-${index}`}
                            disabled={row.livrable === false || row.livrable === '0'}
                          />
                          <label className="form-check-label" htmlFor={`shipping-zone-date-${index}`}>
                            Date
                          </label>
                        </div>
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={row.morocco_only === true || row.morocco_only === '1'}
                            onChange={(event) => updateArrayRow('shipping_zones', index, 'morocco_only', event.target.checked)}
                            id={`shipping-zone-morocco-only-${index}`}
                            disabled={row.livrable === false || row.livrable === '0'}
                          />
                          <label className="form-check-label" htmlFor={`shipping-zone-morocco-only-${index}`}>
                            Maroc
                          </label>
                        </div>
                        <div className="text-muted small">Produits flagués Maroc seulement</div>
                      </td>
                      <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeRow('shipping_zones', index)}>Supprimer</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>

          <div style={tabPanelStyle('mapZones')}>
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <strong>Zones Google Maps livrables</strong>
                <div className="text-muted small">Si une zone est configuree, la localisation client doit etre dans un polygone livrable.</div>
              </div>
              <button className="btn btn-sm btn-outline-primary" onClick={() => addRow('map_zones', newMapZoneRow())}>Ajouter une zone carte</button>
            </div>
            <div className="table-responsive">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th style={{ width: 130 }}>Code</th>
                    <th>Nom</th>
                    <th style={{ width: 160 }}>Type</th>
                    <th style={{ width: 110 }}>Livrable</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {settings.map_zones.length === 0 && (
                    <tr><td colSpan="5" className="text-muted">Aucune zone carte configuree. La carte reste libre si une API key est renseignee.</td></tr>
                  )}
                  {settings.map_zones.map((row, index) => (
                    <tr key={`map-zone-${index}`}>
                      <td>
                        <input
                          className="form-control"
                          value={row.code || ''}
                          onChange={(event) => updateArrayRow('map_zones', index, 'code', event.target.value.toUpperCase())}
                          placeholder="MA001"
                        />
                      </td>
                      <td>
                        <input
                          className="form-control"
                          value={row.name || ''}
                          onChange={(event) => updateArrayRow('map_zones', index, 'name', event.target.value)}
                          placeholder="Casablanca centre"
                        />
                      </td>
                      <td>
                        {normalizePolygonGeometry(row.geometry) ? (
                          <span className="badge bg-success">{polygonPointCount(row.geometry)} points</span>
                        ) : (
                          <span className="badge bg-warning text-dark">Legacy rayon {row.radius_km || '-'} km</span>
                        )}
                      </td>
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            checked={row.livrable !== false && row.livrable !== '0'}
                            onChange={(event) => updateArrayRow('map_zones', index, 'livrable', event.target.checked)}
                            id={`map-zone-livrable-${index}`}
                          />
                          <label className="form-check-label" htmlFor={`map-zone-livrable-${index}`}>
                            Oui
                          </label>
                        </div>
                      </td>
                      <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeRow('map_zones', index)}>Supprimer</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>

          <div style={tabPanelStyle('polygons')}>
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <div>
                <strong>Polygones de livraison</strong>
                <div className="text-muted small">Associez chaque polygone a un code WooCommerce Maroc. Plusieurs polygones peuvent utiliser le meme code, par exemple MA001.</div>
              </div>
              <span className="badge bg-primary">{allPolygonZones.length} zone(s)</span>
            </div>
            <div className="card-body p-0" style={{ maxHeight: 560, overflow: 'auto' }}>
              {allPolygonZones.length === 0 && (
                <div className="p-3 text-muted">Aucun polygone disponible.</div>
              )}
              {deliveryCityGroups.map((group) => {
                const expanded = expandedDeliveryCities[group.cityName] !== false;
                const citySelectValue = group.zoneCodes.length === 1 && group.linkedCount === group.zones.length ? group.zoneCodes[0] : '';
                return (
                  <div key={group.cityName} className="border-bottom">
                    <div className="d-flex align-items-center gap-3 p-3 bg-light">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => toggleDeliveryCity(group.cityName)}
                      >
                        {expanded ? 'Masquer' : 'Afficher'}
                      </button>
                      <div className="flex-grow-1">
                        <div className="fw-bold">{group.cityName}</div>
                        <div className="text-muted small">
                          {group.zones.length} polygone(s), {group.linkedCount} lie(s)
                          {group.zoneCodes.length ? ` - codes: ${group.zoneCodes.join(', ')}` : ' - aucun code'}
                        </div>
                      </div>
                      <div style={{ width: 300 }}>
                        <select
                          className="form-select"
                          value={citySelectValue}
                          onChange={(event) => updateDeliveryCityWooCode(group.cityName, event.target.value === '__clear__' ? '' : event.target.value)}
                        >
                          <option value="">Affecter toute la ville...</option>
                          {(settings.shipping_zones || []).map((wooZone) => (
                            <option key={`${group.cityName}-${wooZone.code}`} value={wooZone.code}>
                              {wooZone.code} - {wooZone.name}
                            </option>
                          ))}
                          {group.linkedCount > 0 && <option value="__clear__">Retirer les liens</option>}
                        </select>
                      </div>
                    </div>
                    {expanded && (
                      <div className="table-responsive">
                        <table className="table mb-0 align-middle">
                          <thead>
                            <tr>
                              <th>Polygone</th>
                              <th style={{ width: 150 }}>Points</th>
                              <th style={{ width: 260 }}>Code WooCommerce Maroc</th>
                              <th style={{ width: 120 }}>Etat</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.zones.map((zone) => (
                              <tr key={zone._polygon_key}>
                                <td>
                                  <strong>{zone.name || zone.code || 'Zone livraison'}</strong>
                                  <div className="text-muted small">
                                    {zone._polygon_source === 'map_settings' ? 'zone_google_maps' : (zone.source || 'delivery_zone')}
                                    {zone.source_id ? ` - ${zone.source_id}` : ''}
                                  </div>
                                </td>
                                <td><span className="badge bg-light text-dark">{polygonPointCount(zone.geometry)} points</span></td>
                                <td>
                                  <select
                                    className="form-select"
                                    value={linkedWooCode(zone)}
                                    onChange={(event) => updatePolygonZoneWooCode(zone, event.target.value)}
                                  >
                                    <option value="">Non lie</option>
                                    {(settings.shipping_zones || []).map((wooZone) => (
                                      <option key={`${zone._polygon_key}-${wooZone.code}`} value={wooZone.code}>
                                        {wooZone.code} - {wooZone.name}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                                <td>
                                  {linkedWooCode(zone) ? (
                                    <span className="badge bg-success">{linkedWooCode(zone)}</span>
                                  ) : (
                                    <span className="badge bg-secondary">Non lie</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          </div>

          <div style={tabPanelStyle('weekly')}>
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Planning hebdomadaire</strong>
              <span className="text-muted small">Jours fixes de la semaine</span>
            </div>
            <div className="table-responsive">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Jour</th>
                    <th>Debut</th>
                    <th>Fin</th>
                    <th>Actif</th>
                  </tr>
                </thead>
                <tbody>
                  {normalizeWeeklyPlanning(settings.weekly_planning).map((row, index) => (
                    <tr key={`weekly-${index}`}>
                      <td>
                        <strong>{weekdayOptions.find((option) => option.value === row.day)?.label || row.day}</strong>
                      </td>
                      <td><input type="time" className="form-control" value={row.from || ''} onChange={(event) => updateArrayRow('weekly_planning', index, 'from', event.target.value)} /></td>
                      <td><input type="time" className="form-control" value={row.to || ''} onChange={(event) => updateArrayRow('weekly_planning', index, 'to', event.target.value)} /></td>
                      <td>
                        <div className="form-check form-switch">
                          <input className="form-check-input" type="checkbox" checked={row.enabled !== false && row.enabled !== '0'} onChange={(event) => updateArrayRow('weekly_planning', index, 'enabled', event.target.checked)} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>

          <div style={tabPanelStyle('dates')}>
          <div className="card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Exceptions par date</strong>
              <button className="btn btn-sm btn-outline-primary" onClick={() => addRow('dates', newDateExceptionRow())}>Ajouter une exception</button>
            </div>
            <div className="table-responsive">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Date precise</th>
                    <th>Debut</th>
                    <th>Fin</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {settings.dates.length === 0 && (
                    <tr><td colSpan="4" className="text-muted">Aucune exception configuree.</td></tr>
                  )}
                  {settings.dates.map((row, index) => (
                    <tr key={`date-exception-${index}`}>
                      <td><input type="date" className="form-control" value={row.date || ''} onChange={(event) => updateArrayRow('dates', index, 'date', event.target.value)} /></td>
                      <td><input type="time" className="form-control" value={row.from || ''} onChange={(event) => updateArrayRow('dates', index, 'from', event.target.value)} /></td>
                      <td><input type="time" className="form-control" value={row.to || ''} onChange={(event) => updateArrayRow('dates', index, 'to', event.target.value)} /></td>
                      <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeRow('dates', index)}>Supprimer</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>

          <div style={tabPanelStyle('holidays')}>
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <strong>Jours feries / indisponibles</strong>
              <button className="btn btn-sm btn-outline-primary" onClick={() => addRow('holidays', newHolidayRow())}>Ajouter une periode</button>
            </div>
            <div className="table-responsive">
              <table className="table mb-0 align-middle">
                <thead>
                  <tr>
                    <th>Du</th>
                    <th>Au</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {settings.holidays.length === 0 && (
                    <tr><td colSpan="3" className="text-muted">Aucune periode indisponible configuree.</td></tr>
                  )}
                  {settings.holidays.map((row, index) => (
                    <tr key={`holiday-${index}`}>
                      <td><input type="date" className="form-control" value={row.from || ''} onChange={(event) => updateArrayRow('holidays', index, 'from', event.target.value)} /></td>
                      <td><input type="date" className="form-control" value={row.to || ''} onChange={(event) => updateArrayRow('holidays', index, 'to', event.target.value)} /></td>
                      <td className="text-end"><button className="btn btn-sm btn-outline-danger" onClick={() => removeRow('holidays', index)}>Supprimer</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          </div>
        </div>
      </div>

      <div style={tabPanelStyle('json')}>
      <div className="card mt-3">
        <div className="card-header"><strong>JSON synchronise</strong></div>
        <pre className="m-0 p-3 bg-light" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(settings, null, 2)}</pre>
      </div>
      </div>
    </div>
  );
}
